import { useState, type ReactNode } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { navigateOptions } from './navigate-options';

interface FilterPillProps {
  label: ReactNode;
  /** How many values are picked; shown as a badge when above zero. */
  count?: number;
  disabled?: boolean;
  /** Leading icon inside the pill. */
  icon?: ReactNode;
  contentClassName?: string;
  /** Controlled open state, for menus that close themselves on a pick. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
}

/** Rounded filter button that opens a menu (kim-ai-frontend's Sentinel Analytics filter pill). */
export function FilterPill({ label, count = 0, disabled = false, icon, contentClassName, open: openProp, onOpenChange, children }: FilterPillProps) {
  const [ownOpen, setOwnOpen] = useState(false);
  const open = openProp ?? ownOpen;
  const setOpen = (next: boolean) => {
    setOwnOpen(next);
    onOpenChange?.(next);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type='button'
          role='combobox'
          aria-expanded={open}
          aria-haspopup='listbox'
          disabled={disabled}
          className='flex h-8 max-w-64 items-center gap-2 rounded-full border border-gray-500 bg-white pr-[11px] pl-[15px] text-[13px] leading-[18px] text-gray-900 focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50'
        >
          {icon}
          <span className='min-w-0 truncate'>{label}</span>
          {count > 0 ? <span className='rounded-full bg-indigo-50 px-1.5 text-[11px] font-medium text-indigo-700'>{count}</span> : null}
          <ChevronDown aria-hidden='true' className='size-3.5 shrink-0' />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align='end'
        sideOffset={8}
        collisionPadding={16}
        className={cn(
          'flex max-h-[min(440px,var(--radix-popover-content-available-height))] max-w-[calc(100vw-2rem)] flex-col rounded-2xl border-0 bg-white/80 px-1.5 py-2.5 font-inter shadow-[inset_0_0_0_1px_var(--color-gray-300),0_3px_12px_rgba(0,0,0,0.08)] backdrop-blur-[6px]',
          contentClassName
        )}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}

interface FilterOptionProps {
  label: string;
  /** Muted text after the label, e.g. an id. */
  hint?: string;
  selected: boolean;
  onSelect: () => void;
}

/** One row of a single-pick filter menu, with a check on the picked row. */
export function FilterOption({ label, hint, selected, onSelect }: FilterOptionProps) {
  return (
    <button
      type='button'
      role='option'
      aria-selected={selected}
      onKeyDown={navigateOptions}
      onClick={onSelect}
      className={cn(
        'flex w-full shrink-0 items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-[13px] leading-[18px] text-text-emphasis outline-none transition-colors hover:bg-gray-100 active:bg-gray-200 focus-visible:bg-gray-100',
        selected && 'bg-gray-150'
      )}
    >
      <span className='min-w-0 flex-1 truncate'>{label}</span>
      {hint ? <span className='shrink-0 text-xs tabular-nums text-gray-800'>{hint}</span> : null}
      <Check aria-hidden='true' className={cn('size-4 shrink-0', !selected && 'invisible')} />
    </button>
  );
}
